import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  TablePagination,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { fetchRoles } from "../../../Redux/Reducers/RoleReducer";
import PageHeader1 from "../../../components/common/PageHeader1";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { getIntParam, getStringParam, setIfTruthy } from "../../../utils/urlState";

// Import the comprehensive privileges configuration
import privilegeConfigData from "../../../config/privileges.json";
const { PRIVILEGES_CONFIG } = privilegeConfigData;

// Create a flat map of privilege values to their human-readable labels
const flatPrivilegeLabelMap = {};
PRIVILEGES_CONFIG.forEach(module => {
  if (module.privileges) {
    module.privileges.forEach(p => {
      flatPrivilegeLabelMap[p.value] = p.label;
    });
  }
  if (module.children) {
    module.children.forEach(child => {
      if (child.privileges) {
        child.privileges.forEach(p => {
          flatPrivilegeLabelMap[p.value] = p.label;
        });
      }
    });
  }
});

function RolePrivilegeList() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { roles = [], loading, pagination } = useSelector((state) => state?.RoleReducer || {});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: getIntParam(searchParams, "page", 0),
    pageSize: getIntParam(searchParams, "pageSize", 10),
  });
  const [searchQuery, setSearchQuery] = useState(getStringParam(searchParams, "search", ""));
  const [startDate, setStartDate] = useState(getStringParam(searchParams, "startDate", null));
  const [endDate, setEndDate] = useState(getStringParam(searchParams, "endDate", null));
  const didInitUrlSyncRef = React.useRef(false);

  const token = localStorage.getItem('astroVani-admin-token');

  useEffect(() => {
    const { page, pageSize } = paginationModel;
    let limit = Number(pageSize);
    if (isNaN(limit)) {
      limit = pagination?.totalDocs || 10000;
    }
    dispatch(fetchRoles({
      page: page + 1,
      limit: limit,
      search: searchQuery,
      startDate: startDate,
      endDate: endDate,
    }));
  }, [paginationModel.page, paginationModel.pageSize, searchQuery, startDate, endDate, dispatch]);

  useEffect(() => {
    if (!didInitUrlSyncRef.current) {
      didInitUrlSyncRef.current = true;
      return;
    }

    const params = new URLSearchParams();
    params.set("page", String(paginationModel.page));
    params.set("pageSize", String(paginationModel.pageSize));
    setIfTruthy(params, "search", searchQuery);
    setIfTruthy(params, "startDate", startDate);
    setIfTruthy(params, "endDate", endDate);
    setSearchParams(params, { replace: true });
  }, [paginationModel.page, paginationModel.pageSize, searchQuery, startDate, endDate, setSearchParams]);

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BASEURL}/admin/role/${selectedId}`,
        {
          headers: {
            Authorization: `${token}`,
          },
        }
      );
      toast.success("Role deleted successfully");
      const { page, pageSize } = paginationModel;
      let limit = Number(pageSize);
      if (isNaN(limit)) {
        limit = pagination?.totalDocs || 10000;
      }
      dispatch(fetchRoles({
        page: page + 1,
        limit: limit,
        search: searchQuery,
        startDate: startDate,
        endDate: endDate,
      }));
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete role");
    } finally {
      setDeleteDialogOpen(false);
      setSelectedId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedId(null);
  };

  const handleChangePage = (event, newPage) => {
    setPaginationModel({ ...paginationModel, page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    const value = event.target.value;
    const newPageSize = parseInt(value, 10);
    setPaginationModel({ page: 0, pageSize: newPageSize });
  };

  const handleResetFilter = () => {
    setSearchQuery('');
    setStartDate(null);
    setEndDate(null);
    setPaginationModel({ page: 0, pageSize: 10 });
  };

  const columns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 80,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
          <IconButton
            size="small"
            component={Link}
            to={`/useree/${row?.id}`}
            title="Edit Role"
            sx={{
              color: '#1976d2',
              '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
            }}
          >
            <i className="icofont-edit" style={{ fontSize: '18px' }}></i>
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteClick(row?.id)}
            title="Delete Role"
            sx={{
              color: '#d32f2f',
              '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' },
            }}
          >
            <i className="icofont-ui-delete" style={{ fontSize: '18px' }}></i>
          </IconButton>
        </Box>
      ),
    },
    { field: "roleName", headerName: "Role Name", width: 250 },
    {
      field: "privileges",
      headerName: "Privileges",
      flex: 1,
      minWidth: 300,
      renderCell: (params) => {
        const privilegesToShow = params.row.privileges || [];
        // Optional: only show 'view' privileges in the table to reduce clutter, or show everything
        // We will show everything but capped at 4 items
        
        return (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", py: 1 }}>
            {privilegesToShow.slice(0, 4).map((priv, idx) => (
              <Chip
                key={idx}
                label={flatPrivilegeLabelMap[priv] || priv}
                size="small"
                color="primary"
                sx={{ fontSize: '12px', height: '24px' }}
              />
            ))}
            {privilegesToShow.length > 4 && (
              <Chip
                label={`+${privilegesToShow.length - 4} more`}
                size="small"
                color="default"
                sx={{ fontSize: '12px', height: '24px' }}
              />
            )}
          </Box>
        )
      },
    },
  ];

  const rows = useMemo(() => {
    const pageSize = paginationModel.pageSize;
    return roles.map((role, index) => ({
      id: role._id,
      sno: paginationModel.page * pageSize + index + 1,
      roleName: role.roleName,
      privileges: role.privileges || [],
    }));
  }, [roles, paginationModel]);

  return (
    <div className="body d-flex">
      <div className="container-xxl">
        <PageHeader1
          righttitle="Add Role"
          link="/addrole"
          routebutton={true}
          pagetitle="Role Privileges"
          classNamembg="mb-3"
        />

        <div className="row g-0 mb-3">
          <div className="col-md-12">
            <div className="card" style={{
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: 'none',
              overflow: 'hidden'
            }}>
              <div className="card-body" style={{ padding: 0 }}>
                <Box sx={{
                  width: '100%',
                  '& .MuiDataGrid-root': { border: 'none' },
                  '& .MuiDataGrid-cell': { borderBottom: '1px solid #f0f0f0' },
                  '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f8f9fa', borderBottom: '2px solid #e9ecef', fontWeight: 600 },
                  '& .MuiDataGrid-footerContainer': { display: 'none' },
                  '& .MuiDataGrid-row:hover': { backgroundColor: '#f8f9fa' },
                }}>
                  <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowId={(row) => row.id}
                    paginationMode="server"
                    hideFooterPagination
                    loading={loading}
                    disableRowSelectionOnClick
                    autoHeight
                    sx={{
                      '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                        outline: 'none',
                      },
                    }}
                  />
                </Box>
                <TablePagination
                  component="div"
                  count={pagination?.totalDocs || 0}
                  page={paginationModel.page}
                  onPageChange={handleChangePage}
                  rowsPerPage={paginationModel.pageSize}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50]}
                  labelRowsPerPage="Rows per page:"
                  sx={{
                    borderTop: '1px solid #e9ecef',
                    '& .MuiTablePagination-toolbar': {
                      padding: '12px 20px',
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this role?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default RolePrivilegeList;